<script lang="ts" setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faPager, faSpinnerThird } from "@fortawesome/pro-light-svg-icons";
import { object, string, array, boolean, mixed, type InferType } from "yup";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import type { StoreTemplate } from "~/models/Template";
import type { MailTemplate } from "~/services/MailTemplateService";
import type { Form } from "#ui/types";
import UIAiButton, { type ButtonAiLoadingStates } from "~/components/ui/UIAiButton.vue";

const show = defineModel<boolean>("show", { required: true });
const store = useMailStore();
const api = useApi();

const schema = object({
    is_enabled: boolean().default(false),
    is_featured: boolean().default(false),
    banner: mixed(),
    title: string().required().min(1).max(255).default(""),
    description: string().required().min(1).max(1024).default(""),
    tags: array().of(string().min(1)).default([]),
});

type Schema = InferType<typeof schema>;

const form = ref<Form<Schema>>();

const state = ref<Schema>({
    is_enabled: false,
    is_featured: false,
    banner: undefined,
    title: "",
    description: "",
    tags: [],
});

const { onSubmit, submitting } = useFormSubmission<Schema>(
    form,
    async (event) => {
        return api.mail_templates.storeTemplate(
            store?.mail.id,
            {
                ...state.value,
            } as StoreTemplate,
        );
    },
);

const { status, data } = useAsyncData(() =>
    api.mail_templates.showTemplate(store?.mail.id),
);

watch(data, (to) => {
    if (to && to.data) {
        state.value.is_enabled = !to.data.deleted_at;
        state.value.is_featured = to.data.is_featured;
        state.value.banner = to.data.banner;
        state.value.title = to.data.title;
        state.value.description = to.data.description;
        state.value.tags = to.data.tags;
    }
});

const populateFieldsByAi = async ({ startLoading, stopLoading }: ButtonAiLoadingStates) => {
    try {
        startLoading();

        const response = await api.mail_templates.generateTemplateFieldsSuggestions(store.mail?.id, {
            title: state.value.title || store.mail?.name || '',
            description: state.value.description || store.mail?.description || '',
            tags: state.value.tags,
        });

        if (response.data) {
            if (response.data.error) {
                useToast().add({
                    title: "Error",
                    description: response.data.error,
                    color: "red",
                });
            }

            state.value.title = response.data.title;
            state.value.description = response.data.description;
            state.value.tags = response.data.tags;
        }
    } catch (e) {
        console.error(e);
        useToast().add({
            title: "Error",
            description: "Failed to populate fields by AI",
            color: "red",
        });
    } finally {
        stopLoading();
    }
}
</script>

<template>
    <AutomationSideCard class="w-96" :on-close="() => (show = false)">
        <template #title>
            <div class="flex items-center gap-3">
                <FontAwesomeIcon :icon="faPager" />
                <span class="flex items-center gap-2">
                    Configure Template
                    <UIAiButton
                        :tooltip="{
                            text: 'Populate fields using AI',
                            popper: { placement: 'bottom' },
                        }"
                        :button="{
                            size: 'sm',
                            color: 'hd-primary',
                            variant: 'ghost',
                        }"
                        @clicked="populateFieldsByAi"
                    />
                </span>
            </div>
        </template>
        <template #footer v-if="status !== 'pending'">
            <div v-if="state.is_enabled" class="grid grid-cols-2 gap-2">
                <UButton color="white" label="Disable Template" size="lg" class="justify-center" @click="() => {
                        state.is_enabled = false;
                        api.mail_templates.disableTemplate(store.mail.id as string);
                    }
                    " />
                <UButton color="primary" label="Save Template" size="lg" class="justify-center" @click="() => {
                        state.is_enabled = true;
                        form?.submit();
                    }
                    ">
                    <template #leading v-if="submitting">
                        <FontAwesomeIcon :icon="faSpinnerThird" spin />
                    </template>
                </UButton>
            </div>
            <UButton v-else-if="!state.is_enabled" color="primary" label="Enable" block size="lg" @click="async () => {
                    state.is_enabled = true;
                    submitting = true;
                    if (data?.data?.deleted_at)
                        await api.automations.enableTemplate(
                            store.mail.id as string,
                        );
                    form?.submit();
                }
                ">
                <template #leading v-if="submitting">
                    <FontAwesomeIcon :icon="faSpinnerThird" spin />
                </template>
            </UButton>
        </template>
        <HdLoader :loading="status === 'pending'">
            <UForm ref="form" :state="state" :schema="schema" :validate-on="['submit']" @submit="onSubmit"
                class="flex flex-col gap-2">
                <UFormGroup label="Feature Template">
                    <template #hint>
                        <UToggle v-model="state.is_featured" />
                    </template>
                </UFormGroup>
                <UFormGroup label="Banner" name="banner">
                    <HdImageInput v-model:src="state.banner" round :icon-height="24" :pintura-aspect-ratio="16 / 9"
                        class="rounded-xl max-h-40 !min-w-full !min-h-32" image-class="object-cover" size="100%" />
                </UFormGroup>
                <UFormGroup label="Title" name="title">
                    <UTextarea size="lg" autoresize v-model="state.title" :placeholder="`${store.name}...`" :rows="2" />
                </UFormGroup>
                <UFormGroup label="Description" name="description">
                    <UTextarea size="lg" autoresize v-model="state.description" :rows="5" />
                </UFormGroup>
                <UFormGroup label="Tags" name="tags">
                    <UFormGroup v-for="(tag, idx) in state.tags" :key="idx" :name="`tags[${idx}]`" class="mt-2 mb-2">
                        <div class="flex items-center gap-2">
                            <UInput v-model="state.tags[idx]" size="lg" class="flex-1" />
                            <UButton class="h-9 w-9 justify-center" color="white" square size="lg" @click="
                                state.tags = state.tags?.filter(
                                    (_, ind) => ind != idx,
                                )
                                ">
                                <FontAwesomeIcon :icon="faMinus" />
                            </UButton>
                        </div>
                    </UFormGroup>
                    <UButton block class="h-9" color="white" size="md" @click="state.tags = [...state.tags, '']">
                        <FontAwesomeIcon :icon="faPlus" />
                    </UButton>
                </UFormGroup>
            </UForm>
        </HdLoader>
    </AutomationSideCard>
</template>
